import {
  Component,
  computed,
  forwardRef,
  inject,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  output,
  OutputEmitterRef,
  Signal
} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {SpinnerComponent} from '../spinner/spinner.component';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {SmartgemPlotsRequestService, SmartgemRoadsRequestService} from '../../openapi';
import {map, of, zip} from 'rxjs';
import {SmartGemCommonService} from '../../services/smart-gem.common.service';
import {SafeUrlPipe} from '../../pipes/safe-url.pipe';
import {Store} from '@ngrx/store';
import {selectApplication} from '../../store/selectors/application.selectors';


export interface ConfirmDrawEvent {
  params: { geometries: any[] };
  type: string | 'confirmDraw';
}

export interface ExposedMapDictRoad {
  roadId?: string | null;
  roadNo?: string | null;
  roadName?: string | null;
  street?: string | null;
  city?: string | null;
}

export type ExposedMapDictPlotType = 'Drogowa' | 'Powiązana';

export interface ExposedMapDictPlot {
  district?: string | null;
  number?: string | null;
  commune?: string | null;
  type?: ExposedMapDictPlotType;
}

export interface ExposedMapDictRate {
  type: string;
  area: number;
  rateId: number;
  rateName: string;
  ratePerSquareMeter: number;
  occupationAreaName: string;
}

export interface ExposedMapDictionary {
  roads: ExposedMapDictRoad[];
  plots: ExposedMapDictPlot[];
  rates: ExposedMapDictRate[];
}

export function getRatesAreaByCodes(rates: ExposedMapDictRate[], ids: number[]): number {
  return rates.filter(v => ids.includes(v.rateId)).at(0)?.area || 0;
}

@Component({
  selector: 'app-exposed-map',
  standalone: true,
  imports: [
    SpinnerComponent,
    SafeUrlPipe
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ExposedMapComponent),
      multi: true
    }
  ],
  templateUrl: './exposed-map.component.html'
})
export class ExposedMapComponent implements OnInit, ControlValueAccessor, OnDestroy {

  public serviceId: InputSignal<string | null> = input<string | null>(null);

  public applicationId: InputSignal<string | null> = input<string | null>(null);

  public ifFrameSrc = environment.exposedMapTargetOrigin;

  private smartGemRoadsService: SmartgemRoadsRequestService = inject(SmartgemRoadsRequestService);

  private smartGemPlotsService: SmartgemPlotsRequestService = inject(SmartgemPlotsRequestService);

  private smartGemCommonService: SmartGemCommonService = inject(SmartGemCommonService);

  private store$: Store = inject(Store);

  private application = this.store$.selectSignal(selectApplication);

  private occupationPurposeId: Signal<number | null> = computed<number | null>(() => {
    const application = this.application();
    return application?.roadOccupationPurpose?.purposeId || application?.service?.roadOccupationPurposes?.at(0)?.purposeId || null;
  });

  public dictionary: OutputEmitterRef<ExposedMapDictionary> = output();

  public disabled: boolean = false;

  public value: string | null = null;

  public onChange: any = () => {
  };

  public onTouch: any = () => {
  };

  public writeValue(value: string | null): void {
    this.value = value;
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public close = output<string | null>();

  private httpClient: HttpClient = inject(HttpClient);

  public loading: number = 0;
  public displayContent = false;

  private loadDictionary(data: ConfirmDrawEvent): void {
    console.log('Application id: ', this.applicationId());
    console.log('Service id: ', this.serviceId());

    this.loading++;

    zip(
      [
        this.smartGemRoadsService.smartgemApiRoadsSmartgemRoadsRequestCollection({
          smartgemRoadsRequest: data
        }),
        this.smartGemPlotsService.smartgemApiPlotsSmartgemPlotsRequestCollection({
          smartgemPlotsRequest: data
        }),
        this.serviceId() && this.applicationId() ? this.smartGemCommonService.getRatesCollection(this.serviceId() || '', this.applicationId() || '', data) : of([])
      ]
    ).pipe(
      map((data) => ({
        roads: (data[0] as any)?.response || [],
        plots: (data[1] as any)?.response || [],
        rates: data[2]
      }))
    ).subscribe({
      next: (res) => {
        this.dictionary.emit(res as ExposedMapDictionary);
        this.loading--;
        setTimeout(() => {
          this.close.emit(this.value);
        })
        this.onChange(this.value);
      },
      error: err => {
        this.loading--;
        setTimeout(() => {
          this.close.emit(this.value);
        })
        this.onChange(this.value);
      }
    });
  }

  onConfirmDraw: (e: MessageEvent) => void = (e: MessageEvent) => {
    if (e.data?.type === 'confirmDraw') {
      const value: ConfirmDrawEvent = e.data as ConfirmDrawEvent;
      this.value = JSON.stringify(value);
      this.loadDictionary(value);
    }
  };

  onContentLoaded: (e: MessageEvent) => void = (e: MessageEvent): void => {
    if (e.data?.type !== 'mapLoaded') {
      return;
    }

    const element: HTMLIFrameElement = document.getElementById('exposed-map') as HTMLIFrameElement;
    const occupationPurposeId: number | null = this.occupationPurposeId();

    if (occupationPurposeId) {
      element.contentWindow?.postMessage(
        {
          type: 'setOccupationPurposeId',
          payload: occupationPurposeId
        },
        this.ifFrameSrc
      );
    }

    const initialGeometries: any[] = this.getInitialGeometries();

    if (initialGeometries.length) {
      element.contentWindow?.postMessage(
        {
          type: 'setInitialGeometries',
          payload: initialGeometries
        },
        this.ifFrameSrc
      );
    }
  };

  // Mapa oczekuje tablicy geometrii [{type, coordinates}], a w value trzymamy cały event confirmDraw
  private getInitialGeometries(): any[] {
    if (!this.value) {
      return [];
    }

    try {
      const parsed = JSON.parse(this.value);
      const geometries = Array.isArray(parsed) ? parsed : parsed?.params?.geometries ?? parsed?.geometries;
      return Array.isArray(geometries) ? geometries : [];
    } catch {
      return [];
    }
  }

  public ngOnInit(): void {
    // Listenery muszą być podpięte przed utworzeniem iframe - mapa wysyła mapLoaded zaraz po zamontowaniu,
    // często zanim iframe odpali zdarzenie load
    window.addEventListener('message', this.onConfirmDraw);
    window.addEventListener('message', this.onContentLoaded);

    this.loading++;
    this.httpClient.post<{ token: string }>(environment.apiBasePath + '/smartgem/login', {}).subscribe({
      next: (response: { token: string }) => {
        sessionStorage.setItem('x-auth-token', response.token);
        sessionStorage.setItem('x-auth-login', 'euslugi');
      },
      complete: () => {
        this.displayContent = true;
        setTimeout(() => {
          const element: HTMLIFrameElement = document.getElementById('exposed-map') as HTMLIFrameElement;
          element.onload = () => {
            this.loading--;
          };
        });
      },
      error: (err: any) => {
        this.loading--;
        this.displayContent = true;
      }
    });
  }

  public ngOnDestroy(): void {
    window.removeEventListener('message', this.onConfirmDraw);
    window.removeEventListener('message', this.onContentLoaded);
  }
}
